import { useState } from 'react';
import { Box, Switch, TextField, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { EWebhookFormat, WebhookService, WebhookSubscription } from '../services/webhook.service';
import { CHAT_SERVICES } from '../constants/chat-services';
import { WebhookEventPicker } from './webhook-event-picker';
import { integrationFieldSx, switchRowSx } from './integration-list.styles';
import { formCardSx, serviceTileSx } from './chat-channel-form.styles';

interface Props {
    edit: WebhookSubscription | null;
    onClose: () => void;
    onSaved: () => void;
}

/// Cadastro e edição de um canal de chat dentro da própria tela, sem modal.
export const ChatChannelForm = ({ edit, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [format, setFormat] = useState<EWebhookFormat>(edit?.format ?? EWebhookFormat.Slack);
    const [name, setName] = useState(edit?.name ?? '');
    const [url, setUrl] = useState(edit?.url ?? '');
    const [events, setEvents] = useState<number[]>(edit?.events ?? []);
    const [active, setActive] = useState(edit?.active ?? true);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState(false);

    const field = integrationFieldSx(cc);

    const save = async () => {
        setTouched(true);

        if (!name.trim() || !url.trim() || events.length === 0) return;

        setSaving(true);

        try {
            const service = new WebhookService();
            const body = { name: name.trim(), url: url.trim(), events, active, format };

            if (edit) await service.update(edit.id, body);
            else await service.create(body);

            showAlert(t(edit ? 'general.atualizacao' : 'general.cadastro'), 'success', 2500);
            onSaved();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={formCardSx(cc)}>
            <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: cc.textPrimary }}>
                    {t(edit ? 'webhooks.editarChat' : 'webhooks.novoChat')}
                </Typography>
                <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                    {t('webhooks.ajudaChat')}
                </Typography>
            </Box>

            <Box>
                <Typography variant="caption" sx={{ color: cc.textSecondary, display: 'block', mb: 0.75 }}>
                    {t('webhooks.servico')}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {CHAT_SERVICES.map((item) => (
                        <Box
                            key={item.value}
                            role="button"
                            tabIndex={0}
                            onClick={() => setFormat(item.value)}
                            onKeyDown={(e) => e.key === 'Enter' && setFormat(item.value)}
                            sx={serviceTileSx(cc, format === item.value)}
                        >
                            <Icon icon={item.icon} height={22} />
                            {item.label}
                        </Box>
                    ))}
                </Box>

                <Typography sx={{ fontSize: 12, color: cc.textMuted, mt: 1 }}>
                    {t(CHAT_SERVICES.find((item) => item.value === format)?.help ?? '')}
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' } }}>
                <TextField
                    required
                    autoFocus
                    size="small"
                    label={t('webhooks.nome')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={Boolean(touched && !name.trim())}
                    helperText={touched && !name.trim() ? t('webhooks.informeONome') : ' '}
                    sx={field}
                />

                <TextField
                    required
                    size="small"
                    label={t('webhooks.url')}
                    placeholder="https://"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    error={Boolean(touched && !url.trim())}
                    helperText={touched && !url.trim() ? t('webhooks.informeAUrl') : ' '}
                    sx={field}
                />
            </Box>

            <WebhookEventPicker
                value={events}
                error={touched && events.length === 0}
                onChange={setEvents}
            />

            <Box sx={switchRowSx(cc)}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary }}>
                    {t('webhooks.ativa')}
                </Typography>
                <Switch checked={active} onChange={() => setActive((value) => !value)} />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton variant="gradient" color="success" onClick={save} disabled={saving}>
                    {t('general.salvar')}
                </MDButton>
            </Box>
        </Box>
    );
};
